import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Typography, Button, Form, message, Steps } from 'antd';
import { ArrowLeftOutlined, FormOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { PageContainer } from './styled';
import FormsService from '../../services/FormsService';
import FormFieldRenderer from '../EventManagement/FormFieldRenderer';
import moment from 'moment';

const { Title, Text } = Typography;
const { Step } = Steps;

const CustomFormsStep = ({ eventType, timeSlot, userInfo, onSubmit, onBack }) => {
  const [forms, setForms] = useState([]);
  const [formResponses, setFormResponses] = useState({});
  const [currentFormIndex, setCurrentFormIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    loadForms();
  }, [eventType]);

  const loadForms = async () => {
    try {
      setLoading(true);
      const formIds = eventType.associatedForms.map(af => af.formId);
      const formPromises = formIds.map(formId => FormsService.getPublicFormById(formId));
      
      const formResults = await Promise.all(formPromises);
      const loadedForms = formResults.filter(result => result.success).map(result => result.data);
      
      setForms(loadedForms);
      
    } catch (error) {
      console.error('Error loading forms:', error);
      message.error('Failed to load forms');
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = (values) => {
    const currentForm = forms[currentFormIndex];
    const responses = {};
    
    currentForm.fields.forEach(field => {
      if (values[field.fieldId] !== undefined) {
        responses[field.fieldId] = values[field.fieldId];
      }
    });
    
    setFormResponses(prev => ({
      ...prev,
      [currentForm._id]: responses
    }));

    if (currentFormIndex < forms.length - 1) {
      setCurrentFormIndex(currentFormIndex + 1);
    } else {
      handleAllFormsComplete();
    }
  };

  const handleAllFormsComplete = () => {
    setSubmitting(true);
    const allResponses = {
      ...formResponses,
      [forms[currentFormIndex]._id]: form.getFieldsValue()
    };
    onSubmit(allResponses);
  };

  const handlePreviousForm = () => {
    if (currentFormIndex > 0) {
      setCurrentFormIndex(currentFormIndex - 1);
    } else {
      onBack();
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '100px 0' }}>
          <FormOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
          <Title level={4} type="secondary">Loading Forms...</Title>
        </div>
      </PageContainer>
    );
  }

  if (forms.length === 0) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '100px 0' }}>
          <CheckCircleOutlined style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }} />
          <Title level={4}>No Additional Forms Required</Title>
          <Button type="primary" onClick={() => onSubmit({})}>
            Continue to Confirmation
          </Button>
        </div>
      </PageContainer>
    );
  }

  const currentForm = forms[currentFormIndex];

  return (
    <PageContainer>
      <Row justify="center" className="custom-forms-wrapper">
        <style>
          {`
            @media (max-width: 768px) {
              .custom-forms-wrapper {
                padding: 16px !important;
              }
              .custom-forms-wrapper .ant-card {
                border-radius: 8px !important;
              }
              .custom-forms-wrapper .ant-card-body {
                padding: 16px !important;
              }
            }
            @media (max-width: 480px) {
              .custom-forms-wrapper {
                padding: 12px !important;
              }
              .custom-forms-wrapper h3 {
                font-size: 20px !important;
              }
              .custom-forms-wrapper .ant-steps {
                margin-bottom: 20px !important;
              }
              .custom-forms-wrapper .ant-steps-item-title {
                font-size: 12px !important;
              }
            }
          `}
        </style>
        <Col xs={24} md={20} lg={16} xl={12}>
          <Card style={{ marginBottom: '24px', border: '1px solid #e8f4f8' }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <FormOutlined style={{ fontSize: '32px', color: '#1890ff', marginBottom: '8px' }} />
              <Title level={3} style={{ margin: '0 0 8px 0' }}>Additional Information</Title>
              <Text type="secondary">
                Please complete the following form{forms.length > 1 ? 's' : ''} for your {eventType.name} appointment
              </Text>
            </div>

            {/* Progress Steps */}
            {forms.length > 1 && (
              <Steps
                current={currentFormIndex}
                style={{ marginBottom: '32px' }}
                size="small"
              >
                {forms.map((formItem) => (
                  <Step key={formItem._id} title={formItem.name} />
                ))}
              </Steps>
            )}

            {/* Appointment Summary */}
            <div className="appointment-summary" style={{
              background: '#f8f9fa',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '24px',
              border: '1px solid #e9ecef'
            }}>
              <style>
                {`
                  @media (max-width: 480px) {
                    .appointment-summary {
                      padding: 12px !important;
                      margin-bottom: 16px !important;
                    }
                    .appointment-summary .ant-col {
                      margin-bottom: 12px !important;
                    }
                  }
                `}
              </style>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Text strong>Appointment:</Text>
                  <div>{eventType.name}</div>
                  <div style={{ color: '#666', fontSize: '12px' }}>
                    {moment(timeSlot.startTime).format('MMMM D, YYYY [at] h:mm A')}
                  </div>
                </Col>
                <Col span={12}>
                  <Text strong>Contact:</Text>
                  <div>{userInfo.firstName} {userInfo.lastName}</div>
                  <div style={{ color: '#666', fontSize: '12px' }}>
                    {userInfo.email}
                  </div>
                </Col>
              </Row>
            </div>

            {/* Current Form */}
            <div style={{ marginBottom: '24px' }}>
              <Title level={4} style={{ marginBottom: '16px' }}>
                {currentForm.name}
                {forms.length > 1 && (
                  <Text type="secondary" style={{ fontWeight: 'normal', marginLeft: '8px' }}>
                    ({currentFormIndex + 1} of {forms.length})
                  </Text>
                )}
              </Title>
              
              {currentForm.description && (
                <Text type="secondary" style={{ display: 'block', marginBottom: '24px' }}>
                  {currentForm.description}
                </Text>
              )}

              <Form
                form={form}
                layout="vertical"
                onFinish={handleFormSubmit}
                key={currentFormIndex} // Force re-render when form changes
                autoComplete="off"
              >
                {currentForm.fields.map(field => (
                  <Form.Item
                    key={field.fieldId}
                    name={field.fieldId}
                    rules={field.required ? [{ required: true, message: `${field.label} is required` }] : []}
                  >
                    <FormFieldRenderer
                      field={field}
                      preview={false}
                    />
                  </Form.Item>
                ))}

                <div className="form-buttons" style={{ textAlign: 'right', marginTop: '32px', borderTop: '1px solid #f0f0f0', paddingTop: '16px' }}>
                  <style>
                    {`
                      @media (max-width: 480px) {
                        .form-buttons {
                          display: flex !important;
                          flex-direction: column-reverse !important;
                          gap: 8px !important;
                        }
                        .form-buttons .ant-btn {
                          width: 100% !important;
                          margin-right: 0 !important;
                        }
                      }
                    `}
                  </style>
                  <Button 
                    style={{ marginRight: '8px' }} 
                    onClick={handlePreviousForm}
                    icon={<ArrowLeftOutlined />}
                  >
                    {currentFormIndex > 0 ? 'Previous Form' : 'Back'}
                  </Button>
                  <Button 
                    type="primary" 
                    htmlType="submit"
                    loading={submitting}
                  >
                    {currentFormIndex < forms.length - 1 ? 'Next Form' : 'Complete Forms'}
                  </Button>
                </div>
              </Form>
            </div>
          </Card>
        </Col>
      </Row>
    </PageContainer>
  );
};

export default CustomFormsStep;
